import {useState} from 'react'
import {Box, Typography, TextField} from '@mui/material'
import {DownloadForOfflineOutlined} from '@mui/icons-material'
import ErrorBoundary from '../../common/ErrorBoundary'
import DateRangePicker from '../DateRangePicker'
import {subDays} from 'date-fns'
import PageTitle from '../../common/PageTitle'
import useWalletRecharge from '../../hooks/useWalletRecharge'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import {formatNumber, parseDateAndTime} from '../../helpers/UtilityHelper'
import {LoadingButton} from '@mui/lab'
import {filterBarSx, primaryBtnSx} from '../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../styles/foundation/colors'
import {primaryTextSx, secondaryTextSx} from '../orders/list2/cellStyles'

const formatDescription = (desc) => {
  if (!desc || typeof desc !== 'string') return '\u2014'
  if (desc.startsWith('VAS charge for::')) {
    try {
      const parts = desc.split('::')
      if (parts.length >= 3) {
        const channel = parts[1]
        const event = parts[2].replace(/_/g, ' ')
        const formatted = event.replace(/\b\w/g, (c) => c.toUpperCase())
        return `VAS: ${channel} \u2013 ${formatted}`
      }
    } catch {
      // fall through
    }
  }
  return desc
}

function WalletHistory({isPassbook = false}) {
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [awb, setAwb] = useState('')
  const [dateRange, setDateRange] = useState([
    {
      endDate: new Date(),
      startDate: subDays(new Date(), 30),
      key: 'selection',
    },
  ])

  const {isLoading, walletRecharge, downloadTransactionFile, isDownloading} = useWalletRecharge({
    dateRange,
    sizePerPage,
    page,
    isPassbook,
    ...(isPassbook && {awb}),
  })

  const columns = [
    {
      field: 'awbNumber',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1,
      minWidth: 150,
      headerName: 'AWB Number',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>
          {row.awbNumber || '\u2014'}
        </Typography>
      ),
    },
    {
      field: 'orderReferenceNumber',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1.2,
      minWidth: 160,
      headerName: 'Reference Number',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{row.orderReferenceNumber || '\u2014'}</Typography>
      ),
    },
    {
      field: 'transactionAmount',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1,
      minWidth: 120,
      headerName: 'Amount',
      renderCell: ({row}) => {
        // Weight-discrepancy hold rows move no money: HOLD reserves the amount
        // against the dispute, RELEASE gives it back, CONVERTED marks the
        // ordinary debit that settled it. Rendered as what they are rather
        // than as a "-0.00" debit.
        if (row.holdEvent === 'HOLD' || row.holdEvent === 'RELEASE') {
          const isHold = row.holdEvent === 'HOLD'
          return (
            <Typography sx={{...secondaryTextSx, color: isHold ? '#b45309' : '#2e7d32'}}>
              {isHold ? 'On hold ' : 'Hold released '}{'₹'}{Number(row.holdAmount || 0).toFixed(2)}
            </Typography>
          )
        }
        const isCredit = row.credit && row.credit > 0
        const amount = isCredit ? row.credit : row.debit
        return (
          <Typography
            sx={{...secondaryTextSx, color: isCredit ? '#2e7d32' : '#c62828'}}
          >
            {isCredit ? '+' : '-'}{Number(amount || 0).toFixed(2)}
            {row.holdEvent === 'CONVERTED' && (
              <Typography component='span' sx={{...secondaryTextSx, color: '#6b7280', ml: 0.5}}>
                (from hold)
              </Typography>
            )}
          </Typography>
        )
      },
    },
    {
      field: 'walletBalance',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1,
      minWidth: 130,
      headerName: 'Wallet Balance',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>
          {'\u20B9'}{Number(row.walletBalance || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'date',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Date & Time',
      flex: 1.2,
      minWidth: 160,
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{parseDateAndTime(row.date)}</Typography>
      ),
    },
    {
      field: 'transactionStatus',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1,
      minWidth: 120,
      headerName: 'Status',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{row.transactionStatus || 'N/A'}</Typography>
      ),
    },
    {
      field: 'transactionSource',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1.2,
      minWidth: 140,
      headerName: 'Source',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{row.transactionSource || 'N/A'}</Typography>
      ),
    },
    {
      field: 'transactionId',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1.5,
      minWidth: 180,
      headerName: 'Transaction ID',
      renderCell: ({row}) => (
        <Typography sx={{fontFamily: 'monospace', fontSize: SIZE.xs, color: SUBTLE}}>
          {row.transactionId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'remark',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1.5,
      minWidth: 180,
      headerName: 'Remark',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{row.remark || 'N/A'}</Typography>
      ),
    },
    {
      field: 'description',
      headerAlign: 'left',
      hideSortIcons: true,
      flex: 1.5,
      minWidth: 180,
      headerName: 'Description',
      renderCell: ({row}) => (
        <Typography sx={secondaryTextSx}>{formatDescription(row.description)}</Typography>
      ),
    },
  ]

  return (
    <ErrorBoundary>
      <PageTitle>{isPassbook ? 'Passbook' : 'Wallet Recharges'}</PageTitle>

      <Box
        sx={{
          ...filterBarSx,
          display: 'flex',
          flexDirection: {xs: 'column', sm: 'row'},
          alignItems: {xs: 'flex-start', sm: 'center'},
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY}}>
          {isPassbook ? 'Transaction History' : 'Recharge History'}
        </Typography>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          {isPassbook && (
            <TextField
              size='small'
              label='Search AWB'
              value={awb}
              onChange={(e) => setAwb(e.target.value)}
              sx={{fontFamily: FONT}}
              InputProps={{
                sx: {fontSize: 12},
              }}
              InputLabelProps={{
                sx: {
                  fontSize: 10,
                  '&:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 12px) scale(1)',
                  },
                },
              }}
            />
          )}
          <DateRangePicker
            defaultRange={dateRange}
            maxDate={new Date()}
            onChange={(range) => setDateRange(range)}
            label='Select date range'
            btnColor='primary'
            triggerSx={{
              fontFamily: FONT,
              fontWeight: WEIGHT.semibold,
              fontSize: SIZE.xs,
              textTransform: 'none',
              px: 2,
              py: 0.75,
            }}
          />
          <LoadingButton
            loading={isDownloading}
            onClick={downloadTransactionFile}
            variant='contained'
            size='small'
            sx={primaryBtnSx}
            startIcon={<DownloadForOfflineOutlined />}
          >
            Export CSV
          </LoadingButton>
        </Box>
      </Box>

      <Box sx={{mt: 1.5}}>
        <ProzoDataGrid
          columnVisibilityModel={{
            awbNumber: isPassbook,
            orderReferenceNumber: isPassbook,
            transactionSource: !isPassbook,
            description: isPassbook,
            transactionId: !isPassbook,
            remark: !isPassbook,
            transactionStatus: !isPassbook,
          }}
          columns={columns}
          autoHeight={true}
          rows={walletRecharge?.map((row, index) => ({...row, id: index})) || []}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          rowCount={Number.MAX_VALUE}
          getRowId={(row) => row.id}
        />
      </Box>
    </ErrorBoundary>
  )
}

export default WalletHistory
